'use server'

import { apiRequest, ApiResponse } from '../request'
import type { ApprovalCompany } from './billing-approval-api'

export type BillingActionType = 'MARK_PAID' | 'CANCEL' | 'SETTLE_PROCEDURE'
export type BillingActionStatus = 'PENDING' | 'APPROVED' | 'REJECTED'

/** Dados lidos do comprovante pela IA (extração + avaliação de autenticidade). */
export interface ReceiptData {
    valor?: number | null
    data_pagamento?: string | null
    beneficiario?: string | null
    pagador?: string | null
    tipo?: string | null
    codigo_transacao?: string | null
    banco?: string | null
    confianca?: number | null
}

/**
 * Payload da solicitação. Além dos dados da baixa, carrega o laudo da leitura do
 * comprovante feita pelo cron `receipt-review-tasks` — ausência de
 * `receiptReviewedAt` significa que a IA ainda não analisou.
 */
export interface BillingActionRequestPayload {
    paidAmount?: number
    paidAt?: string
    /** Quitação com desconto: procedimento alvo. */
    orderId?: number
    /** Quitação: valor combinado do procedimento quando a solicitação foi aberta. */
    agreedAmount?: number
    /** Quitação: diferença que será lançada como desconto. */
    discountAmount?: number
    /** Quitação: total de repasse que a aprovação libera. */
    payoutAmount?: number
    source?: string
    receiptReviewedAt?: string
    receiptReadable?: boolean
    receiptValueMatches?: boolean
    receiptTrustworthy?: boolean
    receiptFindings?: string[]
    receiptData?: ReceiptData | null
}

export interface BillingActionRequestItem {
    /** Filial dona da solicitação — a fila é da organização inteira. */
    company?: ApprovalCompany | null
    id: number
    billingId: number
    action: BillingActionType
    payload?: BillingActionRequestPayload | null
    status: BillingActionStatus
    createdAt: string
    requestedBy: { id: number; name: string } | null
    billing: {
        id: number
        type: 'RECEIVABLE' | 'PAYABLE'
        status: string
        amount: number
        dueDate: string
        description: string | null
        paymentMethod: string | null
        /** Há comprovante anexado? Só então a IA emite veredito sobre o documento. */
        hasReceipt?: boolean
        /** Comprovante em análise (último anexo ainda válido), para o aprovador abrir. */
        receipt?: { url: string; name: string } | null
        supplier?: { id: number; name: string } | null
        client?: { id: number; name: string } | null
        partner?: { id: number; name: string; tradingName?: string | null } | null
    }
}

const BASE = 'financial/billing-action-requests'

export type ApprovedBillingActionRequestItem = BillingActionRequestItem & {
    approvedBy?: { id: number; name: string } | null
    approvedAt?: string | null
}

export async function getApprovedBillingActionRequests(
    page = 1,
    limit = 20,
    search?: string,
): Promise<
    ApiResponse<{
        items: ApprovedBillingActionRequestItem[]
        total: number
        page: number
        limit: number
    }>
> {
    return apiRequest('financial/billing-action-requests/history', {
        params: { page, limit, ...(search ? { search } : {}) },
    })
}

export async function getPendingBillingActionRequests(
    search?: string,
): Promise<ApiResponse<BillingActionRequestItem[]>> {
    return apiRequest<BillingActionRequestItem[]>(BASE, {
        params: search ? { search } : {},
    })
}

export async function approveBillingActionRequest(
    id: number,
): Promise<ApiResponse<BillingActionRequestItem>> {
    return apiRequest<BillingActionRequestItem>(`${BASE}/${id}/approve`, {}, 'post')
}

export async function rejectBillingActionRequest(
    id: number,
    reason: string,
    reasonCategory: RejectReasonCategory,
): Promise<ApiResponse<BillingActionRequestItem>> {
    return apiRequest<BillingActionRequestItem>(
        `${BASE}/${id}/reject`,
        { data: { reason, reasonCategory } },
        'post',
    )
}

/**
 * Tira a solicitação da fila sem decidir nada no lançamento (pedido duplicado ou
 * aberto por engano). Diferente de recusar, que é decisão e fica no histórico.
 * Exige `delete` em `financial:billing_approvals`.
 */
export async function deleteBillingActionRequest(
    id: number,
): Promise<ApiResponse<{ id: number; billingId: number }>> {
    return apiRequest(`${BASE}/${id}`, {}, 'delete')
}

/** Ver `BulkResult` em `billing-approval-api`: lote parcial volta com HTTP 200. */
export type { BulkResult } from './billing-approval-api'

export async function bulkApproveBillingActionRequests(
    ids: number[],
): Promise<ApiResponse<import('./billing-approval-api').BulkResult>> {
    return apiRequest(`${BASE}/bulk-approve`, { data: { ids } }, 'post')
}

/**
 * `RECEIPT` invalida o comprovante analisado — ele não poderá ser reenviado nesta
 * cobrança. `OTHER` recusa só a solicitação e mantém o comprovante utilizável.
 */
export type RejectReasonCategory = 'RECEIPT' | 'OTHER'

export async function bulkRejectBillingActionRequests(
    ids: number[],
    reason: string,
    reasonCategory: RejectReasonCategory,
): Promise<ApiResponse<import('./billing-approval-api').BulkResult>> {
    return apiRequest(
        `${BASE}/bulk-reject`,
        { data: { ids, reason, reasonCategory } },
        'post'
    )
}
